import {
  CSSProperties,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  FunctionComponent,
} from 'react';
import {
  FloatingPanel,
  type FloatingPanelProps,
  type FloatingPanelRef,
  Mask,
} from 'antd-mobile';
import { useResizeDetector } from 'react-resize-detector';
import { createPortal } from 'react-dom';
import { twMerge } from 'src/utils/twMerge';
import SafeAreaBottom from 'src/designSystem/SafeArea/SafeAreaBottom';
import { PortalContainerContext } from 'src/contexts/PortalContainerContext';

const FLOATING_HEADER_HEIGHT = 36;

type Props = Omit<FloatingPanelProps, 'anchors'> & {
  isOpen: boolean;
  onClose?: () => void;
  closable?: boolean;
  isBackdropHidden?: boolean;
  onPanelHeightChange?: (height: number) => void;
  'data-testid'?: string;
  priority?: number;
};

export const AutoFloatingPanel: FunctionComponent<Props> = ({
  onClose,
  closable = true,
  isBackdropHidden = false,
  onPanelHeightChange,
  'data-testid': dataTestId,
  priority = 0,
  ...props
}) => {
  const [anchors, setAnchors] = useState<number[]>([0]);
  const panelRef = useRef<FloatingPanelRef | null>(null);
  const portalContainer = useContext(PortalContainerContext);

  const { ref } = useResizeDetector({
    onResize: ({ height }) => {
      if (!height) {
        return;
      }
      const totalHeight = height + FLOATING_HEADER_HEIGHT;
      onPanelHeightChange?.(totalHeight);
      if (totalHeight > 0 && anchors[1] !== totalHeight) {
        setAnchors([0, totalHeight]);
      }
    },
  });

  useLayoutEffect(() => {
    if (props.isOpen) {
      if (anchors[1] !== undefined) {
        panelRef.current?.setHeight(anchors[1]);
      }
    } else {
      panelRef.current?.setHeight(0);
    }
  }, [anchors, props.isOpen]);

  const onHeightChange = useCallback(
    (height: number) => {
      if (!closable) {
        return;
      }
      // height <= 0 is a real collapse only once a resize measurement landed (anchors[1] defined).
      if (onClose && props.isOpen && height <= 0 && anchors[1] !== undefined) {
        onClose();
      }
    },
    [closable, onClose, props.isOpen, anchors],
  );

  return createPortal(
    <>
      {!closable ? (
        <style>
          .main-nav-bar,.adm-safe-area-position-top {`{ z-index: var(--adm-modal-z-index) }`}
        </style>
      ) : null}
      <Mask
        className={'h-screen supports-[height:100dvh]:h-dvh bottom-0 top-0'}
        style={{
          zIndex: `calc(var(--z-index) + ${priority})`,
        }}
        visible={props.isOpen && !isBackdropHidden}
        onMaskClick={onClose}
        data-testid={
          dataTestId
            ? `${dataTestId}-mask-${props.isOpen ? 'open' : 'closed'}`
            : undefined
        }
      />
      <FloatingPanel
        {...props}
        className={twMerge('baobab-panel', props.className)}
        style={{
          zIndex: `calc(var(--z-index) + ${priority})`,
          '--header-height': `${FLOATING_HEADER_HEIGHT}px`,
        } as CSSProperties}
        onHeightChange={onHeightChange}
        anchors={closable ? anchors : [anchors[1] ?? 0]}
        ref={panelRef}
        data-testid={
          dataTestId
            ? `${dataTestId}-${props.isOpen ? 'open' : 'closed'}`
            : undefined
        }
      >
        <div ref={ref} className={'max-h-[calc(100vh-100px)] supports-[height:100dvh]:max-h-[calc(100dvh-100px)]'}>
          <div
            inert={!props.isOpen}
            className="flex flex-col gap-6 px-[var(--panel-px)] pb-6 [--panel-px:theme(spacing.4)] empty:hidden"
          >
            {props.children}
          </div>
          <SafeAreaBottom />
        </div>
      </FloatingPanel>
    </>,
    portalContainer || document.body,
  );
};
